import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'

export interface RecaptchaEnterprise {
  execute: (action: string) => Promise<string>
}

const LOAD_TIMEOUT_MS = 10000
const READY_TIMEOUT_MS = 5000
// Exported so the spec binds to the real budget instead of a copied literal.
// Worst case for a fully unresponsive Google is LOAD + READY + EXECUTE = 25s before
// useRecaptcha's fail-open path runs the payment untokened. Long, but bounded --
// which the unbounded execute() was not.
export const EXECUTE_TIMEOUT_MS = 10000

const withTimeout = <T>(p: Promise<T>, ms: number, message: string): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms)
  })
  return Promise.race([p, timeout]).finally(() => { if (timer) { clearTimeout(timer) } }) as Promise<T>
}

export default defineNuxtPlugin(() => {
  const config = useTypedRuntimeConfig()
  const siteKey = config.public.RECAPTCHA_KEY

  // siteKey is passed at the call site rather than via `scripts.registry` in
  // nuxt.config on purpose: a registry siteKey lives at
  // runtimeConfig.public.scripts.googleRecaptcha.siteKey, which only
  // NUXT_PUBLIC_SCRIPTS_GOOGLE_RECAPTCHA_SITE_KEY can override. The deploy
  // injects NUXT_PUBLIC_RECAPTCHA_KEY, so a registry key would be silently
  // ignored and the build-time .env value would ship to every environment.
  //
  // trigger: 'manual' keeps enterprise.js off non-payment pages — useRecaptcha
  // short-circuits before execute() when the recaptchaDisabled kill switch is set,
  // so the opt-out never fetches the script at all.
  const script = siteKey
    ? useScriptGoogleRecaptcha({
      siteKey,
      enterprise: true,
      scriptOptions: { trigger: 'manual' }
    })
    : undefined

  const loadApi = async (instance: NonNullable<typeof script>) => {
    // useScript caches its load promise and RESOLVES it with `false` on script
    // error rather than rejecting, so a failed first attempt would otherwise
    // poison every later call. reload() re-injects, preserving the
    // retry-on-failure the payment forms depend on.
    const load = instance.status.value === 'error' ? instance.reload() : instance.load()
    const api = await withTimeout(load, LOAD_TIMEOUT_MS, 'recaptcha: enterprise script load timed out')
    if (!api) { throw new Error('recaptcha: enterprise script failed to load') }
    return api
  }

  const recaptcha: RecaptchaEnterprise = {
    async execute (action: string) {
      if (!script || !siteKey) { throw new Error('recaptcha: siteKey not configured') }
      const { grecaptcha } = await loadApi(script)
      const enterprise = grecaptcha?.enterprise
      if (!enterprise) { throw new Error('recaptcha: enterprise namespace missing after load') }
      await withTimeout(
        new Promise<void>(resolve => enterprise.ready(resolve)),
        READY_TIMEOUT_MS,
        'recaptcha: enterprise.ready() timed out'
      )
      // Bounded like load and ready above. grecaptcha.execute() returns a bare promise
      // with no timeout of its own, so a Google endpoint that accepts the connection but
      // never responds would hang the payment forever -- useRecaptcha's fail-open catch
      // cannot fire on a promise that never settles.
      return await withTimeout(
        enterprise.execute(siteKey, { action }),
        EXECUTE_TIMEOUT_MS,
        'recaptcha: enterprise execute() timed out'
      )
    }
  }

  return {
    provide: { recaptcha }
  }
})
